import React, {useState} from 'react';
import {motion} from 'framer-motion';
import {Link} from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {FiBookOpen, FiClock, FiCheck, FiStar, FiPlay, FiLock, FiFlag, FiShield, FiTrendingUp, FiInfo, FiAward, FiUsers, FiTarget, FiChevronRight} = FiIcons;

const Learning = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [completedModules, setCompletedModules] = useState(new Set([1, 3]));

  const categories = [
    {id: 'all', name: 'All Modules'},
    {id: 'history', name: 'American History'},
    {id: 'government', name: 'Government'},
    {id: 'philosophy', name: 'Philosophy'},
    {id: 'economics', name: 'Economics'},
    {id: 'republican', name: 'Republican Party'}
  ];

  const modules = [
    {
      id: 1,
      title: 'The Founding Fathers: Principles and Vision',
      category: 'history',
      duration: '45 min',
      difficulty: 'Beginner',
      description: 'Explore the revolutionary ideas and principles that shaped America.',
      progress: 100,
      locked: false,
      path: '/learning'
    },
    {
      id: 2,
      title: 'Understanding the Constitution',
      category: 'government',
      duration: '60 min',
      difficulty: 'Intermediate',
      description: 'Deep dive into the Constitution and its enduring relevance.',
      progress: 0,
      locked: false,
      path: '/learning'
    },
    {
      id: 3,
      title: 'Conservative Economic Principles',
      category: 'economics',
      duration: '40 min',
      difficulty: 'Beginner',
      description: 'Learn about free markets, limited government, and fiscal responsibility.',
      progress: 100,
      locked: false,
      path: '/learning'
    },
    {
      id: 4,
      title: 'Federalism and States\' Rights',
      category: 'government',
      duration: '35 min',
      difficulty: 'Intermediate',
      description: 'Understanding the balance between federal and state power.',
      progress: 25,
      locked: false,
      path: '/learning'
    },
    {
      id: 5,
      title: 'The American Revolution: Causes and Impact',
      category: 'history',
      duration: '50 min',
      difficulty: 'Beginner',
      description: 'Discover what led to America\'s fight for independence.',
      progress: 0,
      locked: false,
      path: '/learning'
    },
    {
      id: 6,
      title: 'Advanced Constitutional Law',
      category: 'government',
      duration: '90 min',
      difficulty: 'Advanced',
      description: 'Complex constitutional interpretations and landmark cases.',
      progress: 0,
      locked: true,
      path: '/learning'
    },
    {
      id: 7,
      title: 'The Republican Party: History and Values',
      category: 'republican',
      duration: '55 min',
      difficulty: 'Beginner',
      description: 'Learn about the Grand Old Party\'s history, values, achievements, and what it stands for.',
      progress: 0,
      locked: false,
      path: '/learning/republican-party',
      featured: true,
      image: 'https://www.hoover.org/sites/default/files/research/images/istock-1269930175.jpeg'
    },
    {
      id: 8,
      title: 'Republican Presidents Through History',
      category: 'republican',
      duration: '60 min',
      difficulty: 'Intermediate',
      description: 'Explore the legacies of notable Republican presidents from Lincoln to Trump.',
      progress: 0,
      locked: false,
      path: '/learning'
    },
    {
      id: 9,
      title: 'GOP Policy Positions',
      category: 'republican',
      duration: '40 min',
      difficulty: 'Beginner',
      description: 'Understanding Republican policy positions on key issues facing America today.',
      progress: 0,
      locked: false,
      path: '/learning'
    }
  ];

  const filteredModules = selectedCategory === 'all' ? modules : modules.filter(module => module.category === selectedCategory);

  // Extract featured modules
  const featuredModules = modules.filter(module => module.featured);

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'Beginner': return 'text-green-600 bg-green-100';
      case 'Intermediate': return 'text-yellow-600 bg-yellow-100';
      case 'Advanced': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const handleModuleClick = (moduleId, path) => {
    if (!modules.find(m => m.id === moduleId)?.locked) {
      // In a real app, this would navigate to the module content
      console.log(`Opening module ${moduleId}`);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{opacity: 0, y: 30}}
          animate={{opacity: 1, y: 0}}
          transition={{duration: 0.6}}
          className="text-center mb-12"
        >
          <h1 className="text-4xl font-bold text-patriot-navy mb-4">Learning Hub</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Master the principles that built America. Learn about our history, government, and conservative values through comprehensive, engaging modules.
          </p>
        </motion.div>

        {/* Featured Module */}
        {featuredModules.length > 0 && (
          <motion.div
            initial={{opacity: 0, y: 30}}
            animate={{opacity: 1, y: 0}}
            transition={{duration: 0.6, delay: 0.1}}
            className="mb-12"
          >
            <h2 className="text-2xl font-bold text-patriot-navy mb-6">Featured Learning Module</h2>
            <div className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300">
              <div className="grid grid-cols-1 md:grid-cols-3">
                <div className="md:col-span-1">
                  <img 
                    src={featuredModules[0].image || "https://www.hoover.org/sites/default/files/research/images/istock-1269930175.jpeg"} 
                    alt={featuredModules[0].title} 
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="md:col-span-2 p-6">
                  <div className="flex items-center mb-2">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getDifficultyColor(featuredModules[0].difficulty)}`}>
                      {featuredModules[0].difficulty}
                    </span>
                    <span className="ml-2 text-sm text-gray-600 flex items-center">
                      <SafeIcon icon={FiClock} className="h-4 w-4 mr-1" />
                      {featuredModules[0].duration}
                    </span>
                  </div>
                  <h3 className="text-xl font-bold text-patriot-navy mb-2">{featuredModules[0].title}</h3>
                  <p className="text-gray-600 mb-6">{featuredModules[0].description}</p>
                  <Link
                    to={featuredModules[0].path}
                    className="inline-flex items-center px-4 py-2 bg-patriot-red text-white rounded-lg hover:bg-patriot-red-dark transition-colors duration-200"
                  >
                    <SafeIcon icon={FiPlay} className="h-4 w-4 mr-2" />
                    Start Learning
                  </Link>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-lg p-6 shadow-md">
            <div className="flex items-center">
              <SafeIcon icon={FiBookOpen} className="h-8 w-8 text-patriot-red mr-3" />
              <div>
                <div className="text-2xl font-bold text-patriot-navy">
                  {completedModules.size}/{modules.length}
                </div>
                <div className="text-gray-600">Modules Completed</div>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg p-6 shadow-md">
            <div className="flex items-center">
              <SafeIcon icon={FiClock} className="h-8 w-8 text-patriot-red mr-3" />
              <div>
                <div className="text-2xl font-bold text-patriot-navy">12.5</div>
                <div className="text-gray-600">Hours Learned</div>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg p-6 shadow-md">
            <div className="flex items-center">
              <SafeIcon icon={FiStar} className="h-8 w-8 text-patriot-red mr-3" />
              <div>
                <div className="text-2xl font-bold text-patriot-navy">85%</div>
                <div className="text-gray-600">Average Score</div>
              </div>
            </div>
          </div>
        </div>

        {/* Category Filter */}
        <div className="mb-8">
          <div className="flex flex-wrap gap-2">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`px-4 py-2 rounded-lg font-medium transition-colors duration-200 ${
                  selectedCategory === category.id
                    ? 'bg-patriot-red text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100'
                }`}
              >
                {category.name}
              </button>
            ))}
          </div>
        </div>

        {/* Modules Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredModules.map((module, index) => (
            <motion.div
              key={module.id}
              initial={{opacity: 0, y: 30}}
              animate={{opacity: 1, y: 0}}
              transition={{duration: 0.6, delay: index * 0.1}}
              className={`bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300 ${
                module.locked ? 'opacity-75' : 'cursor-pointer'
              }`}
              onClick={() => !module.locked && handleModuleClick(module.id, module.path)}
            >
              <Link to={module.locked ? '#' : module.path} className="block">
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center">
                      {module.locked ? (
                        <SafeIcon icon={FiLock} className="h-6 w-6 text-gray-400 mr-2" />
                      ) : completedModules.has(module.id) ? (
                        <SafeIcon icon={FiCheck} className="h-6 w-6 text-green-500 mr-2" />
                      ) : (
                        <SafeIcon icon={FiPlay} className="h-6 w-6 text-patriot-red mr-2" />
                      )}
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getDifficultyColor(module.difficulty)}`}>
                      {module.difficulty}
                    </span>
                  </div>
                  <h3 className="text-lg font-semibold text-patriot-navy mb-2">
                    {module.title}
                  </h3>
                  <p className="text-gray-600 text-sm mb-4">{module.description}</p>
                  <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                    <span className="flex items-center">
                      <SafeIcon icon={FiClock} className="h-4 w-4 mr-1" />
                      {module.duration}
                    </span>
                  </div>

                  {/* Progress Bar */}
                  {module.progress > 0 && (
                    <div className="mb-4">
                      <div className="flex justify-between text-xs text-gray-600 mb-1">
                        <span>Progress</span>
                        <span>{module.progress}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-patriot-red h-2 rounded-full transition-all duration-300"
                          style={{width: `${module.progress}%`}}
                        ></div>
                      </div>
                    </div>
                  )}

                  <button
                    className={`w-full py-2 px-4 rounded-lg font-medium transition-colors duration-200 ${
                      module.locked
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : completedModules.has(module.id)
                        ? 'bg-green-100 text-green-700 hover:bg-green-200'
                        : 'bg-patriot-red text-white hover:bg-patriot-red-dark'
                    }`}
                    disabled={module.locked}
                  >
                    {module.locked ? 'Locked' : completedModules.has(module.id) ? 'Review' : 'Start Learning'}
                  </button>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>

        {/* Republican Party Learning Path */}
        <motion.div
          initial={{opacity: 0, y: 30}}
          animate={{opacity: 1, y: 0}}
          transition={{duration: 0.6, delay: 0.2}}
          className="mt-12 bg-patriot-navy rounded-lg p-8 text-white"
        >
          <div className="flex flex-col md:flex-row md:items-center md:justify-between">
            <div className="mb-6 md:mb-0 md:mr-6">
              <h2 className="text-2xl font-bold mb-2">Republican Party Learning Path</h2>
              <p className="text-gray-300 mb-4">
                Deepen your understanding of the Republican Party's history, values, and impact on America with our comprehensive learning path.
              </p>
              <div className="flex flex-wrap gap-3">
                <div className="flex items-center bg-white bg-opacity-10 rounded-lg px-3 py-2">
                  <SafeIcon icon={FiFlag} className="h-4 w-4 mr-2 text-patriot-gold" />
                  <span className="text-sm">Party History</span>
                </div>
                <div className="flex items-center bg-white bg-opacity-10 rounded-lg px-3 py-2">
                  <SafeIcon icon={FiShield} className="h-4 w-4 mr-2 text-patriot-gold" />
                  <span className="text-sm">Core Values</span>
                </div>
                <div className="flex items-center bg-white bg-opacity-10 rounded-lg px-3 py-2">
                  <SafeIcon icon={FiAward} className="h-4 w-4 mr-2 text-patriot-gold" />
                  <span className="text-sm">Key Achievements</span>
                </div>
              </div>
            </div>
            <div className="flex justify-center">
              <Link
                to="/learning/republican-party"
                className="inline-flex items-center px-6 py-3 bg-patriot-red text-white rounded-lg hover:bg-patriot-red-dark transition-colors duration-200"
              >
                <span>Start Learning Path</span>
                <SafeIcon icon={FiChevronRight} className="ml-2 h-5 w-5" />
              </Link>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Learning;